/**
 * Brazilian Currency Formatter Utility
 * Provides smart auto-fill with comma and zero formatting
 */

/**
 * Format number to Brazilian currency display (R$ 1.234,56)
 * @param {number|string} value - The value to format
 * @returns {string} Formatted currency string
 */
export const formatCurrencyBR = (value) => {
  if (value === null || value === undefined || value === '') return '';
  
  // Convert to number first
  const num = typeof value === 'number' ? value : parseFloat(String(value)?.replace(/[^0-9,-]/g, '')?.replace(',', '.'));
  
  if (isNaN(num)) return '';
  
  // Format with Brazilian locale
  return num?.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Format input value as user types (smart auto-fill)
 * Handles: typing numbers, pasting, deleting
 * @param {string} value - Current input value
 * @returns {string} Formatted value for display
 */
export const formatCurrencyInput = (value) => {
  if (!value) return '';
  
  // Remove all non-numeric characters except comma and dot
  let cleaned = String(value)?.replace(/[^0-9,]/g, '');
  
  // Remove multiple commas, keep only the last one
  const parts = cleaned?.split(',');
  if (parts?.length > 2) {
    cleaned = parts?.slice(0, -1)?.join('') + ',' + parts?.[parts?.length - 1];
  }
  
  // Limit decimal places to 2
  if (cleaned?.includes(',')) {
    const [integer, decimal] = cleaned?.split(',');
    cleaned = integer + ',' + (decimal || '')?.slice(0, 2);
  }
  
  // Add thousand separators to integer part
  if (cleaned?.includes(',')) {
    const [integer, decimal] = cleaned?.split(',');
    const formattedInteger = integer?.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return formattedInteger + ',' + decimal;
  } else {
    // No decimal yet, just format integer
    return cleaned?.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }
};

/**
 * Parse Brazilian formatted currency to number
 * Handles: "1.234,56", "R$ 1.234,56", "1234,56"
 * @param {string} value - Formatted currency string
 * @returns {number} Parsed number value
 */
export const parseCurrencyBR = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  
  // Remove R$, spaces, and thousand separators (dots)
  let cleaned = String(value)?.replace(/R\$/g, '')?.replace(/\s/g, '')?.replace(/\./g, '')?.replace(',', '.'); // Replace comma with dot for decimal
  
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : 0;
};

/**
 * Handle input change with auto-formatting
 * @param {string} value - Raw input value
 * @param {function} setValue - State setter function
 */
export const handleCurrencyChange = (value, setValue) => {
  const formatted = formatCurrencyInput(value);
  setValue(formatted);
};

/**
 * Add currency symbol prefix
 * @param {string} value - Formatted value
 * @returns {string} Value with R$ prefix
 */
export const addCurrencySymbol = (value) => {
  if (!value) return '';
  return `R$ ${value}`;
};
